
import { ref, Ref } from "vue";
import { Component, Vue, Watch, Prop } from "vue-property-decorator";

import { IRouteMeta } from "@/router/interface";
import program_routes from "@/api/routes/programs";

import { Debounce } from "@/helpers";

import CInfiniteScroll from "@/components/InfiniteScroll.vue";
import CStructure, { IStructureProp } from "@/components/Structure.vue";

import StructureFactory from "@/models/structure/factory";

export interface IProgramAssignmentAvailableUser {
  id: string;
  full_name: string;
  login: string;
  identification_number: string;
  email: string;
}

@Component({
  name: "CProgramAssignmentsEditUsers",
  components: {
    CInfiniteScroll,
    CStructure
  }
})
export default class CProgramAssignmentsEditUsers extends Vue {
  @Prop({ required: true }) public user_ids!: string[];
  @Prop({ required: true }) public factories!: StructureFactory[];

  public selected_factory: StructureFactory | null = null;
  public structure: IStructureProp = {};

  private users_is_loading: boolean = false;
  private users_current_page: number = 0;
  private users_next_page: boolean = true;
  private users_per_page: number = 100;
  public users_search: string = "";
  public available_users: IProgramAssignmentAvailableUser[] = [];
  public selected_users: Ref<Set<string>> = ref(new Set<string>());

  public show_filter: boolean = false;
  public filters_count: number = 0;
  public filters: Map<string, boolean> = new Map();

  protected async created() {
    this.selected_users.value = new Set(this.user_ids);

    await this.clearUsers();
  }

  private async clearUsers() {
    this.available_users = [];

    await this.loadUsers();
  }

  public async clearFilters() {
    this.filters.clear();

    this.selected_factory = null;
    this.structure = {};

    this.filters_count = this.filters.size;

    await this.clearUsers();
  }

  public async changeFactoryFilter() {
    if (this.selected_factory) {
      this.filters.set("factory", true);
    } else {
      this.filters.delete("factory");
    }

    this.filters_count = this.filters.size;

    await this.clearUsers();
  }

  public async changeStructure(structure: IStructureProp) {
    this.structure = structure;

    if (this.structure?.infinity_id) {
      this.filters.set("infinity", true);
    } else {
      this.filters.delete("infinity");
    }

    this.filters_count = this.filters.size;

    await this.clearUsers();
  }

  @Watch("users_search")
  @Debounce(500)
  protected async searchUsers() {
    await this.clearUsers();
  }

  public changeSelectedUsers(user_id: string) {
    if (this.selected_users.value.has(user_id)) {
      this.selected_users.value.delete(user_id);
    } else {
      this.selected_users.value.add(user_id);
    }

    this.$emit("change", Array.from(this.selected_users.value));
  }

  public async usersInfiniteHandler() {
    if (this.users_next_page && !this.users_is_loading) {
      await this.loadUsers(this.users_current_page + 1);
    }
  }

  private async loadUsers(page: number = 0) {
    this.users_is_loading = true;

    return this.$api
      .get(program_routes.assignment_available_users, {
        params: {
          page,
          per_page: this.users_per_page,
          search: this.users_search,
          factory_id: this.selected_factory?.id,
          infinity_id: this.structure?.infinity_id,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: IProgramAssignmentAvailableUser[] }) => {
        this.available_users.push(...res);

        this.users_current_page = page;
        this.users_next_page = res.length === this.users_per_page;
      })
      .finally(() => {
        this.users_is_loading = false;
      });
  }

  public allowWrite() {
    return this.$api.allowWrite();
  }
}
